import { Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { compactMoney, date, money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';

export default function CallsPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const live = scope.calls.filter((call) => call.status === 'ISSUED');
  const outstanding = sumBy(
    live.flatMap((call) => call.lines),
    (line) => line.amount - line.paid,
  );
  const uncalled = sumBy(scope.holdings, (holding) => holding.uncalled ?? 0);
  const committed = sumBy(scope.holdings, (holding) => holding.commitment ?? 0);
  return (
    <>
      <PageHeader
        title="Capital calls"
        subtitle="Draw down commitments: calculate every holder's share, issue notices to the app and email, and track payment."
        actions={
          <Button component={RouterLink} to="/manager/calls/new" variant="contained">
            New capital call
          </Button>
        }
      />
      <AboutThisPage>
        <p>
          Calls apply to commitment funds (OPP1 in the demo). A call is drafted here, approved by a
          second person, then notices go to every holder with a unique BPAY reference. Payments
          arriving in the bank feed are matched automatically by reference; units are issued as
          capital is paid.
        </p>
      </AboutThisPage>
      <StatGrid>
        <StatCard label="Total commitments" value={compactMoney(committed)} />
        <StatCard
          label="Called and paid"
          value={compactMoney(committed - uncalled)}
          hint={
            committed
              ? `${Math.round(((committed - uncalled) / committed) * 100)}% drawn`
              : undefined
          }
        />
        <StatCard label="Uncalled" value={compactMoney(uncalled)} tone="info" />
        <StatCard
          label="Outstanding on open calls"
          value={compactMoney(outstanding)}
          tone={outstanding ? 'warning' : 'neutral'}
        />
      </StatGrid>
      <Paper variant="outlined">
        <DataTable
          rows={scope.calls}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/manager/calls/${row.id}`)}
          defaultSort={{ key: 'issued', direction: 'desc' }}
          columns={[
            {
              key: 'call',
              label: 'Call',
              sortValue: (row) => row.number,
              render: (row) => (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {scope.fundById.get(row.fundId)?.code} Call {row.number} · {row.callPct}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.purpose}
                  </Typography>
                </>
              ),
            },
            {
              key: 'issued',
              label: 'Issued',
              sortValue: (row) => row.issueDate,
              render: (row) => date(row.issueDate),
            },
            {
              key: 'due',
              label: 'Due',
              sortValue: (row) => row.dueDate,
              render: (row) => date(row.dueDate),
            },
            {
              key: 'amount',
              label: 'Called',
              align: 'right',
              render: (row) =>
                row.lines.length ? money(sumBy(row.lines, (line) => line.amount)) : 'Imported',
            },
            {
              key: 'progress',
              label: 'Received',
              render: (row) => {
                if (!row.lines.length)
                  return (
                    <Typography variant="caption" color="text.secondary">
                      Pre-migration
                    </Typography>
                  );
                const due = sumBy(row.lines, (line) => line.amount);
                const paid = sumBy(row.lines, (line) => line.paid);
                return (
                  <Stack spacing={0.5} sx={{ minWidth: 140 }}>
                    <LinearProgress
                      variant="determinate"
                      value={due ? (paid / due) * 100 : 0}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {money(paid)} · {row.lines.filter((line) => line.status === 'PAID').length}/
                      {row.lines.length} holders
                    </Typography>
                  </Stack>
                );
              },
            },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
          ]}
        />
      </Paper>
    </>
  );
}
